import { supabase } from './supabaseService';
import { UserAvailability } from '../types';

class AvailabilityService {
  async getUserAvailability(userId: string): Promise<UserAvailability | null> {
    try {
      console.log('🔄 Loading availability for user:', userId);

      const { data, error } = await supabase
        .from('user_availability')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('❌ Error loading availability:', error);

        const fallbackKey = `user_availability_${userId}`;
        const saved = localStorage.getItem(fallbackKey);
        if (saved) {
          console.log('⚠️ Using localStorage fallback for availability');
          const data = JSON.parse(saved);
          return {
            ...data,
            createdAt: new Date(data.createdAt),
            updatedAt: new Date(data.updatedAt)
          };
        }

        return null;
      }

      if (!data) {
        console.log('ℹ️ No availability found for user:', userId);

        const fallbackKey = `user_availability_${userId}`;
        const saved = localStorage.getItem(fallbackKey);
        if (saved) {
          console.log('⚠️ Migrating availability from localStorage to database');
          const localData = JSON.parse(saved);
          const migrated = await this.saveUserAvailability(userId, localData);
          return migrated;
        }

        return null;
      }

      const availability: UserAvailability = {
        id: data.id,
        userId: data.user_id,
        timezone: data.timezone,
        isActive: data.is_active,
        isVisibleInPublicAgenda: data.is_visible_in_public_agenda,
        dailySchedules: data.daily_schedules || {},
        breaks: data.breaks || [],
        blockedDates: data.blocked_dates || [],
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };

      console.log('✅ Availability loaded:', userId);
      return availability;
    } catch (error) {
      console.error('❌ Error loading user availability:', error);

      const fallbackKey = `user_availability_${userId}`;
      const saved = localStorage.getItem(fallbackKey);
      if (saved) {
        console.log('⚠️ Using localStorage fallback due to error');
        const data = JSON.parse(saved);
        return {
          ...data,
          createdAt: new Date(data.createdAt),
          updatedAt: new Date(data.updatedAt)
        };
      }

      return null;
    }
  }

  async saveUserAvailability(userId: string, availability: Partial<UserAvailability>): Promise<UserAvailability> {
    try {
      console.log('💾 Saving availability for user:', userId, availability);

      const availabilityData = {
        user_id: userId,
        timezone: availability.timezone || 'Europe/Madrid',
        is_active: availability.isActive !== false,
        is_visible_in_public_agenda: availability.isVisibleInPublicAgenda !== false,
        daily_schedules: availability.dailySchedules || {},
        breaks: availability.breaks || [],
        blocked_dates: availability.blockedDates || []
      };

      const { data, error } = await supabase
        .from('user_availability')
        .upsert(availabilityData, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error saving availability:', error);

        const fallbackKey = `user_availability_${userId}`;
        const fallbackData: UserAvailability = {
          id: availability.id || `avail_${userId}`,
          userId,
          timezone: availability.timezone || 'Europe/Madrid',
          isActive: availability.isActive !== false,
          isVisibleInPublicAgenda: availability.isVisibleInPublicAgenda !== false,
          dailySchedules: availability.dailySchedules || {},
          breaks: availability.breaks || [],
          blockedDates: availability.blockedDates || [],
          createdAt: availability.createdAt || new Date(),
          updatedAt: new Date()
        };
        localStorage.setItem(fallbackKey, JSON.stringify(fallbackData));
        console.log('⚠️ Saved to localStorage as fallback');

        throw error;
      }

      const result: UserAvailability = {
        id: data.id,
        userId: data.user_id,
        timezone: data.timezone,
        isActive: data.is_active,
        isVisibleInPublicAgenda: data.is_visible_in_public_agenda,
        dailySchedules: data.daily_schedules || {},
        breaks: data.breaks || [],
        blockedDates: data.blocked_dates || [],
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };

      console.log('✅ Availability saved successfully');
      return result;
    } catch (error) {
      console.error('❌ Error saving user availability:', error);
      throw error;
    }
  }

  async deleteUserAvailability(userId: string): Promise<void> {
    try {
      console.log('🗑️ Deleting availability for user:', userId);

      const { error } = await supabase
        .from('user_availability')
        .delete()
        .eq('user_id', userId);

      if (error) {
        console.error('❌ Error deleting availability:', error);
        throw error;
      }

      const fallbackKey = `user_availability_${userId}`;
      localStorage.removeItem(fallbackKey);

      console.log('✅ Availability deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting user availability:', error);
      throw error;
    }
  }

  async getAvailableUsersForDate(date: Date): Promise<any[]> {
    try {
      console.log('📅 Getting available users for date:', date.toISOString());

      const dayOfWeek = date.getDay();
      const dateString = date.toISOString().split('T')[0];

      const { data: availabilities, error } = await supabase
        .from('user_availability')
        .select(`
          *,
          users:user_id (
            id,
            name,
            email,
            avatar,
            is_active
          )
        `)
        .eq('is_active', true)
        .eq('is_visible_in_public_agenda', true);

      if (error) {
        console.error('❌ Error loading availabilities:', error);
        return [];
      }

      const availableUsers = [];

      for (const avail of availabilities || []) {
        if (!avail.users || !avail.users.is_active) continue;

        const dailySchedules = avail.daily_schedules || {};
        const daySchedule = dailySchedules[dayOfWeek];

        if (daySchedule && daySchedule.isWorking) {
          const blockedDates = avail.blocked_dates || [];
          if (!blockedDates.includes(dateString)) {
            availableUsers.push({
              ...avail.users,
              availability: {
                id: avail.id,
                userId: avail.user_id,
                timezone: avail.timezone,
                isActive: avail.is_active,
                isVisibleInPublicAgenda: avail.is_visible_in_public_agenda,
                dailySchedules: avail.daily_schedules,
                breaks: avail.breaks,
                blockedDates: avail.blocked_dates,
                createdAt: new Date(avail.created_at),
                updatedAt: new Date(avail.updated_at)
              }
            });
          }
        }
      }

      console.log(`✅ Found ${availableUsers.length} available users`);
      return availableUsers;
    } catch (error) {
      console.error('❌ Error getting available users:', error);
      return [];
    }
  }

  async getAvailableTimeSlots(userId: string, date: Date): Promise<string[]> {
    try {
      const availability = await this.getUserAvailability(userId);

      if (!availability || !availability.isActive) {
        return [];
      }

      if (!availability.isVisibleInPublicAgenda) {
        return [];
      }

      const dayOfWeek = date.getDay();
      const daySchedule = availability.dailySchedules?.[dayOfWeek];

      if (!daySchedule || !daySchedule.isWorking) {
        return [];
      }

      const dateString = date.toISOString().split('T')[0];
      if (availability.blockedDates.includes(dateString)) {
        return [];
      }

      const slots = [];
      const startTime = this.timeStringToMinutes(daySchedule.start);
      const endTime = this.timeStringToMinutes(daySchedule.end);

      for (let minutes = startTime; minutes < endTime; minutes += 30) {
        const timeString = this.minutesToTimeString(minutes);

        const conflictsWithBreak = availability.breaks.some(brk => {
          if (brk.day !== undefined && brk.day !== dayOfWeek) {
            return false;
          }

          const breakStart = this.timeStringToMinutes(brk.start);
          const breakEnd = this.timeStringToMinutes(brk.end);
          return minutes >= breakStart && minutes < breakEnd;
        });

        if (!conflictsWithBreak) {
          slots.push(timeString);
        }
      }

      return slots;
    } catch (error) {
      console.error('❌ Error getting available time slots:', error);
      return [];
    }
  }

  private timeStringToMinutes(timeString: string): number {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private minutesToTimeString(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  async isUserAvailable(userId: string, datetime: Date): Promise<boolean> {
    try {
      const availability = await this.getUserAvailability(userId);

      if (!availability || !availability.isActive) {
        return false;
      }

      if (!availability.isVisibleInPublicAgenda) {
        return false;
      }

      const dayOfWeek = datetime.getDay();
      const daySchedule = availability.dailySchedules?.[dayOfWeek];

      if (!daySchedule || !daySchedule.isWorking) {
        return false;
      }

      const dateString = datetime.toISOString().split('T')[0];
      if (availability.blockedDates.includes(dateString)) {
        return false;
      }

      const timeString = datetime.toTimeString().slice(0, 5);
      const timeMinutes = this.timeStringToMinutes(timeString);
      const startMinutes = this.timeStringToMinutes(daySchedule.start);
      const endMinutes = this.timeStringToMinutes(daySchedule.end);

      if (timeMinutes < startMinutes || timeMinutes >= endMinutes) {
        return false;
      }

      const conflictsWithBreak = availability.breaks.some(brk => {
        if (brk.day !== undefined && brk.day !== dayOfWeek) {
          return false;
        }

        const breakStart = this.timeStringToMinutes(brk.start);
        const breakEnd = this.timeStringToMinutes(brk.end);
        return timeMinutes >= breakStart && timeMinutes < breakEnd;
      });

      return !conflictsWithBreak;
    } catch (error) {
      console.error('❌ Error checking user availability:', error);
      return false;
    }
  }
}

export const availabilityService = new AvailabilityService();
